import { Css, Palette, useTestIds } from "@homebound/beam";
import { BarDatum } from "@nivo/bar";
import { useMemo } from "react";
import { BarChart } from "src/components/charts/BarChart";
import { getNumberTicks } from "src/components/charts/utils";
import { ProjectChangeEventGraphType } from "src/generated/graphqlTypes";
import { formatCentsToPrice, pluralize, truncateString } from "src/utils";
import { VarianceTab_ChartDataFragment } from "../VarianceTab.generated";

type TopChangeEventsWidgetProps = {
  type: ProjectChangeEventGraphType;
  data: VarianceTab_ChartDataFragment[];
};

export function TopChangeEventsWidget({ data, type }: TopChangeEventsWidgetProps) {
  const tid = useTestIds({});
  const chartData = useMemo(() => mapToChartData(data), [data]);
  // Get y-axis ticks based on the chart data
  const yTicks = useMemo(
    () =>
      chartData.isEmpty
        ? [0, 5000_00, 10000_00, 15000_00, 20000_00] // Fallback ticks for empty data
        : getNumberTicks(
            chartData.map((v) => Number(v.value)),
            5,
          ),
    [chartData],
  );

  return (
    <div css={Css.w100.mw0.hPx(320).p3.bgWhite.ba.bcGray300.bw1.br8.$}>
      <div {...tid.title} css={Css.md.gray900.pb3.df.$}>
        Top 10 {type === ProjectChangeEventGraphType.Reason ? "Reason Names" : "Cost Divisions"}
      </div>
      <div css={Css.h("85%").w100.$} {...tid.BarChart}>
        <BarChart
          dataset={chartData.isEmpty ? emptyDataset : chartData}
          keys={chartData.isEmpty ? ["empty_data"] : chartData.map((v) => v.toKeys().first!)}
          colors={
            chartData.isEmpty
              ? [Palette.Transparent]
              : [Palette.Blue600, ...Array.from({ length: 9 }, () => Palette.Blue300)]
          }
          emptyStateTooltipText={chartData.isEmpty ? "There are no projects that have started yet." : undefined}
          tooltipValueFormat={(object) =>
            type === ProjectChangeEventGraphType.Reason
              ? `Reason code "${object.data.id}" amounts to ${formatCentsToPrice(Number(object.data.value))} and was used ${object.data.timesUsed} ${pluralize(Number(object.data.timesUsed), "time")}.`
              : `Cost division "${object.data.id}" used ${formatCentsToPrice(Number(object.data.value))}.`
          }
          margin={{ left: 60, top: 5, bottom: 70, right: 5 }}
          padding={0.22}
          disabled={chartData.isEmpty}
          gridYValues={yTicks}
          enableLabel={false}
          axisLeft={{
            tickValues: yTicks,
            tickPadding: 20,
            tickSize: 0,
            format: (value: number) => formatCentsToPrice(value, { trim: true, rounded: true }),
          }}
          axisBottom={{
            tickSize: 0,
            tickPadding: 18,
            tickRotation: -40,
            format: (value: string) => truncateString(value, 15),
          }}
        />
      </div>
    </div>
  );
}

function mapToChartData(data: VarianceTab_ChartDataFragment[]): BarDatum[] {
  return data.flatMap((row) => ({
    [row.name]: row.totalCostInCents, // Add key for the bar
    id: row.name,
    value: row.totalCostInCents,
    timesUsed: row.timesUsed ?? 0,
  }));
}

const emptyDataset = [{ empty_data: 20000_00, id: "" }];
